import type { UseMutationResult, UseQueryResult } from "@tanstack/react-query"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"

import { queryKeys } from "~/api/cache"
import type { SolarSungageApplicationReadDto } from "~/api/dto/solar-sungage.dto"
import { applySolarSungageApplicationState } from "~/api/hooks/solar-sungage-application-cache"
import useResumeSolarSungageApplication from "~/api/hooks/useResumeSolarSungageApplication"
import { solarProposalService } from "~/api/services/solar-proposal.service"
import {
  SOLAR_REFRESH_PRICING_REQUIRED_CODE,
  SOLAR_SUNGAGE_SELLER_NOT_CERTIFIED_CODE,
  solarProposalErrorCode,
  solarProposalErrorMessage,
} from "~/api/solar-proposal-errors"

import type { SungageApplicationStatusBundle } from "../ProposalSetup/financing/sungage/SungageApplicationStatus"

export const SOLAR_SUNGAGE_HOSTED_EYEBROW = `Sungage Solar`
export const START_SOLAR_SUNGAGE_LABEL = `Start Sungage application`
export const START_SOLAR_SUNGAGE_BUSY_LABEL = `Setting up application`
export const REPLACE_SOLAR_SUNGAGE_LABEL = `Start a new Sungage application`
export const SOLAR_SUNGAGE_NO_APPLICATION_PROMPT = `No Sungage Existing Loan Application yet. Start the hosted application from the current Sungage Quote.`
export const SOLAR_SUNGAGE_REPLACES_PROMPT = `The Sungage program changed since this application started. A new application replaces it; the current one stays on record.`
export const SOLAR_SUNGAGE_REFRESH_REQUIRED_PROMPT = `Refresh Pricing first: the Sungage Quote no longer matches this proposal.`
export const SOLAR_SUNGAGE_SELLER_NOT_CERTIFIED_PROMPT = `Seller isn't certified with Sungage. Complete Sungage certification, then start the application again.`
export const SOLAR_SUNGAGE_COMMAND_FAILED_PROMPT = `Unable to reach Sungage right now. The last saved application is unchanged.`
export const SOLAR_SUNGAGE_LAST_START_FAILED_LABEL = `The last start did not reach Sungage`

const usd = new Intl.NumberFormat(`en-US`, {
  style: `currency`,
  currency: `USD`,
})

/** Why a Sungage command failed: a stale Quote, an uncertified seller, or the server's reason. */
export const solarSungageCommandErrorMessage = (error: unknown): string => {
  const code = solarProposalErrorCode(error)
  if (code === SOLAR_REFRESH_PRICING_REQUIRED_CODE) {
    return SOLAR_SUNGAGE_REFRESH_REQUIRED_PROMPT
  }
  if (code === SOLAR_SUNGAGE_SELLER_NOT_CERTIFIED_CODE) {
    return SOLAR_SUNGAGE_SELLER_NOT_CERTIFIED_PROMPT
  }
  return solarProposalErrorMessage(error) ?? SOLAR_SUNGAGE_COMMAND_FAILED_PROMPT
}

/** The current application from the stored read (the drawer's other observer reads Start's gate). */
const useSolarSungageApplicationRead = (
  proposalId: string,
): UseQueryResult<SolarSungageApplicationReadDto | null> =>
  useQuery({
    queryKey: queryKeys.solarProposal.sungageApplication(proposalId),
    queryFn: () => solarProposalService.getSungageApplication(proposalId),
    select: (state) => state.application,
    enabled: proposalId.length > 0,
    retry: 1,
    staleTime: 30_000,
    refetchOnWindowFocus: false,
  })

/** Refresh, answering with the application it reconciled. */
const useRefreshSolarSungageRead = (): UseMutationResult<
  SolarSungageApplicationReadDto | null,
  unknown,
  { proposalId: string; applicationId: string }
> => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ proposalId, applicationId }) => {
      const state = await solarProposalService.refreshSungageApplication(
        proposalId,
        applicationId,
      )
      applySolarSungageApplicationState(queryClient, proposalId, state)
      return state.application
    },
  })
}

/**
 * Solar Proposal reads and commands for the shared Sungage status: the
 * proposal's current application, Refresh (polled while the drawer is open)
 * and Resume on Solar's endpoints, with the amount due and the escalation
 * under each payment.
 */
export const solarSungageStatusBundle: SungageApplicationStatusBundle<SolarSungageApplicationReadDto> =
  {
    testIdPrefix: `solar`,
    programLabel: (application) => application.submittedTerms.fundingGroupLabel,
    hostedEyebrow: SOLAR_SUNGAGE_HOSTED_EYEBROW,
    useApplication: useSolarSungageApplicationRead,
    useRefresh: useRefreshSolarSungageRead,
    useResume: useResumeSolarSungageApplication,
    describeError: solarSungageCommandErrorMessage,
    paymentExtras: (application, terms) => {
      const submitted = application.submittedTerms
      const amountDue =
        terms === `approved`
          ? (application.approvedTerms?.loanAmount ?? null)
          : submitted.amountDue
      return [
        { label: `Program`, value: submitted.fundingGroupLabel },
        ...(amountDue != null
          ? [{ label: `Amount due`, value: usd.format(amountDue) }]
          : []),
        ...(submitted.escalatorPercentage != null
          ? [
              {
                label: `Yearly escalation`,
                value: `${submitted.escalatorPercentage}%`,
              },
            ]
          : []),
      ]
    },
  }
